/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  PayInvoicePayload,
  PayInvoiceResponse,
  IsPaidPayload,
  IsPaidResponse,
} from './types';
import { apiClient } from '@/utils/apiClient';

const coercePaidBoolean = (input: any): boolean => {
  if (typeof input === 'boolean') return input;
  if (typeof input === 'number') return input === 1;

  if (typeof input === 'string') {
    const t = input.trim().toLowerCase();
    if (t === 'true' || t === '1') return true;
    if (t === 'false' || t === '0') return false;
  }

  if (input && typeof input === 'object') {
    if ('paid' in input) return Boolean(input.paid);
    if ('success' in input) return Boolean(input.success);
    if ('is_paid' in input) return Boolean(input.is_paid);
    return false;
  }
  return Boolean(input);
};

export const postPayInvoice = createAsyncThunk<
  PayInvoiceResponse,
  PayInvoicePayload,
  { rejectValue: string }
>('payInvoice/postPayInvoice', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient('/payments/api/v1/checkout/', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    return (response ?? {}) as PayInvoiceResponse;
  } catch (err: any) {
    const res = err?.response?.data ?? err?.response ?? err;
    const msg =
      (Array.isArray(res?.error) && res.error.join(' ')) ||
      res?.error?.message ||
      res?.error ||
      res?.message ||
      res?.detail ||
      'Payment failed';
    return rejectWithValue(String(msg));
  }
});

export const getIsPaid = createAsyncThunk<
  IsPaidResponse,
  IsPaidPayload,
  { rejectValue: string }
>('payInvoice/getIsPaid', async ({ number }, { rejectWithValue }) => {
  try {
    const url = `/payments/api/v1/is_paid/?${new URLSearchParams({
      number,
    }).toString()}`;
    const response = await apiClient(url, { method: 'GET' });

    const paid = coercePaidBoolean(response);
    const message =
      (typeof response === 'object' &&
        (response?.message || response?.detail || response?.status)) ||
      undefined;

    return { paid, message, raw: response };
  } catch (err: any) {
    const res = err?.response?.data ?? err?.response ?? err;
    const msg =
      (Array.isArray(res?.error) && res.error.join(' ')) ||
      res?.error?.message ||
      res?.error ||
      res?.message ||
      res?.detail ||
      'Could not check payment status';
    return rejectWithValue(String(msg));
  }
});
